'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { FetchBlogsQuery } from '@eliadmin/core/src/api/blogs/blogs-api';
import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { useBlogCategories } from '@eliadmin/core/src/components/blog/blog-cards';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getTextFromHTML } from '@eliadmin/core/src/utils/misc-utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import Image from 'next/image';
import Link from '@eliadmin/core/src/components/link';
import { useSearchParams } from 'next/navigation';
import { RiArrowRightSLine } from 'react-icons/ri';

type FirstBlogProps = {
  query?: FetchBlogsQuery;
};
export default function FirstBlog(props: FirstBlogProps) {
  const { t } = useTranslations();

  const locale = useLocaleStore(state => state.currentLocale);
  const searchParams = useSearchParams();

  const { data: blogs } = useSuspenseQuery({
    queryKey: ['blogs', { locale, query: props.query }],
    queryFn: () =>
      fetchBlogs({
        locale,
        query: { ...props.query, results: 1 },
      }),
  });

  /** TODO: top blogs filter */
  const textContent = getTextFromHTML(blogs.data[0].content).replace(/&nbsp;/gm, '');
  const categories = blogs?.data[0]?.categories;

  const activeCategoryIds = useBlogCategories(s => s.activeCategoryIds);

  if (activeCategoryIds.length > 0 || searchParams.get('search')) return null;

  return (
    <>
      <div
        className="
        flex
        w-full
        flex-col
        gap-4
    "
      >
        <Link
          href={ROUTES.BLOG_DETAIL({ slug: blogs?.data[0].slug })}
          aria-label={blogs?.data[0]?.name}
          className="relative h-[460px] w-full rounded-[10px]"
        >
          <Image
            src={blogs?.data[0]?.image?.url || '/images/placeholder.png'}
            alt={resolveImageAlt(blogs?.data?.[0]?.image, blogs?.data?.[0]?.name)}
            style={{ objectFit: 'cover' }}
            className="rounded-[10px]"
            fill
            priority
            fetchPriority="high"
          />
        </Link>
        <div className="flex w-full items-center justify-between">
          <div className="flex w-full items-center justify-start gap-1">
            {categories.map(category => (
              <Link
                key={category?.id}
                href={`/blog?filter=categories.id IN [${category?.id}]`}
                className="
                h-auto
                rounded-full
                bg-foreground
                px-4
                py-1
                text-[10px]
                font-semibold
                text-white
            "
              >
                {category?.name}
              </Link>
            ))}
          </div>
          <Link
            href={ROUTES.BLOG_DETAIL({ slug: blogs?.data[0].slug })}
            className="whitespace-nowrap text-xs text-foreground"
          >
            {t('text592') + ' ' + new Date(blogs?.data[0]?.created_at).toLocaleDateString(locale)}
          </Link>
        </div>
        <Link href={ROUTES.BLOG_DETAIL({ slug: blogs?.data[0].slug })}>
          <h2 data-ea-visual-tag="h1" className="text-3xl font-bold text-foreground">{blogs?.data[0]?.name}</h2>
        </Link>
        <Link
          href={ROUTES.BLOG_DETAIL({ slug: blogs?.data[0].slug })}
          className="my-1 line-clamp-3 overflow-y-hidden text-ellipsis text-sm text-foreground"
        >
          {textContent}
        </Link>
        <Link
          href={ROUTES.BLOG_DETAIL({ slug: blogs?.data[0].slug })}
          aria-label={`${t('text591')}: ${blogs?.data[0]?.name}`}
          className="
          flex
          h-[36px]
          w-full
          items-center
          justify-center
          gap-2
          rounded-full
          bg-[#1eadea18]
          text-sm
          text-foreground
      "
        >
          {t('text591')}
          <RiArrowRightSLine size={16} />
        </Link>
      </div>
      <Separator className="h-[1px] w-full" />
    </>
  );
}
